import type { AnalyticsBrowser } from '@segment/analytics-next';
import Cookies from 'js-cookie';

declare global {
  interface Window {
    rugietSessionId?: string;
  }
}

interface SegmentSessionCookie {
  id: string;
  start_ts: number;
  last_activity: number;
}

const SESSION_COOKIE_KEY = 'rugiet_session';
const SESSION_TTL = 30 * 60 * 1000; // 30 minutes (in milliseconds)
const SESSION_COOKIE_DOMAIN = import.meta.env['VITE_SEGMENT_SESSION_COOKIE_DOMAIN'];

function getSessionData(): SegmentSessionCookie {
  return JSON.parse(Cookies.get(SESSION_COOKIE_KEY) || '{}');
}

function setSessionCookie(value: SegmentSessionCookie) {
  const inThirtyMinutes = new Date(Date.now() + SESSION_TTL);
  Cookies.set(SESSION_COOKIE_KEY, JSON.stringify(value), {
    expires: inThirtyMinutes,
    path: '/',
    ...(SESSION_COOKIE_DOMAIN ? { domain: SESSION_COOKIE_DOMAIN } : {}),
    secure: true,
    sameSite: 'None',
  });
}

function getUTMParam(key: string) {
  return new URLSearchParams(window.location.search).get(key);
}

function getSessionInfo() {
  const now = Date.now();
  const storedSession = getSessionData();
  const createNewSession = !storedSession.id || now - storedSession.last_activity > SESSION_TTL;
  let isNewSession = false;

  if (createNewSession) {
    const newSession: SegmentSessionCookie = {
      id: crypto.randomUUID(),
      start_ts: now,
      last_activity: now,
    };
    setSessionCookie(newSession);
    isNewSession = true;
    return { ...newSession, isNewSession };
  }

  storedSession.last_activity = now;
  setSessionCookie(storedSession);
  return { ...storedSession, isNewSession };
}

export function initializeSession(analytics: AnalyticsBrowser) {
  const { id: session_id, start_ts, isNewSession } = getSessionInfo();
  window.rugietSessionId = session_id;

  // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/middleware/
  analytics.addSourceMiddleware(({ payload, next }) => {
    // Inject session_id into every event
    if (payload.obj.properties) {
      payload.obj.properties['session_id'] = session_id;
    } else if (payload.obj.traits) {
      payload.obj.traits['session_id'] = session_id;
    }
    next(payload);
  });

  if (isNewSession) {
    analytics.track('Session Started', {
      session_id,
      start_ts: new Date(start_ts).toISOString(),
      referrer: document.referrer,
      entry_url: window.location.href,
      utm_source: getUTMParam('utm_source'),
      utm_medium: getUTMParam('utm_medium'),
      utm_campaign: getUTMParam('utm_campaign'),
      utm_term: getUTMParam('utm_term'),
      utm_content: getUTMParam('utm_content'),
    });
  }
}
